import React, { useState, useRef, useEffect } from "react";
import {
  Box, Button, Typography, Paper, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, TablePagination, IconButton, Snackbar
} from "@mui/material";
import { Add, Edit, Delete, Undo } from "@mui/icons-material";
import { axiosInstances } from "./apiConfig";
import InvoiceQuotationApp from "./InvoiceQuotationApp"; // 👈 Your Add/Edit Invoice component

export default function Invoice() {
  const [invoices, setInvoices] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [total, setTotal] = useState(0);

  const deleteTimers = useRef({});
  const [pendingDeletes, setPendingDeletes] = useState({});
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // 🔹 Load invoices from backend
  const loadInvoices = async () => {
    try {
      const res = await axiosInstances.invoiceApi.get(`/?page=${page}&size=${rowsPerPage}`);
      const result = res.data.data || {};
      setInvoices(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error("Error fetching invoices", err);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 🔹 Open Add/Edit Invoice Form
  const openInvoiceForm = (invoice = null) => {
    setSelectedInvoice(invoice);
    setShowInvoiceForm(true);
  };

  // 🔹 Delete invoice (with undo option)
  const handleDelete = (id) => {
    setPendingDeletes((prev) => ({ ...prev, [id]: true }));

    deleteTimers.current[id] = setTimeout(async () => {
      try {
        await axiosInstances.invoiceApi.post(`/delete`, { id });
        setSnackbar({ open: true, message: "Invoice deleted successfully!", severity: "success" });
        loadInvoices();
      } catch (err) {
        console.error("Delete failed", err);
        setSnackbar({ open: true, message: "Something went wrong!", severity: "error" });
      } finally {
        setPendingDeletes((prev) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
        deleteTimers.current[id] = null;
      }
    }, 30000);
  };

  const handleUndo = (id) => {
    if (deleteTimers.current[id]) {
      clearTimeout(deleteTimers.current[id]);
      deleteTimers.current[id] = null;
    }
    setPendingDeletes((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  // 🔹 Render Invoice Form if adding or editing
  if (showInvoiceForm) {
    return (
      <InvoiceQuotationApp
        invoiceId={selectedInvoice?.id}
        invoiceData={selectedInvoice}
        onCancel={() => {
          setShowInvoiceForm(false);
          setSelectedInvoice(null);
          loadInvoices();
        }}
      />
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Invoice Management</Typography>

      <Button
        variant="contained"
        color="primary"
        startIcon={<Add />}
        onClick={() => openInvoiceForm()}
      >
        Add Invoice
      </Button>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sr. No.</TableCell>
              <TableCell>Invoice ID</TableCell>
              <TableCell>Invoice No</TableCell>
              <TableCell>Order ID</TableCell>
              <TableCell>Customer ID</TableCell>
              <TableCell>Invoice Date</TableCell>
              <TableCell>SubTotal</TableCell>
              <TableCell>Tax (%)</TableCell>
              <TableCell>Discount</TableCell>
              <TableCell>Grand Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {invoices.map((invoice, index) => (
              <TableRow key={invoice.id}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{invoice.id}</TableCell>
                <TableCell>{invoice.invoiceNo}</TableCell>
                <TableCell>{invoice.orderId}</TableCell>
                <TableCell>{invoice.customerId}</TableCell>
                <TableCell>{invoice.invoiceDate}</TableCell>
                <TableCell>{invoice.subTotal?.toFixed(2)}</TableCell>
                <TableCell>
                  igst: {invoice.igst * 100 || "0"} <br />
                  cgst: {invoice.cgst * 100 || "0"} <br />
                  sgst: {invoice.sgst * 100 || "0"}
                </TableCell>
                <TableCell>{invoice.discount?.toFixed(2) || 0}</TableCell>
                <TableCell>{invoice.grandTotal?.toFixed(2)}</TableCell>
                <TableCell>{invoice.inv_sts}</TableCell>

                <TableCell>
                  <IconButton color="primary" onClick={() => openInvoiceForm(invoice)}>
                    <Edit />
                  </IconButton>

                  {pendingDeletes[invoice.id] ? (
                    <IconButton color="warning" onClick={() => handleUndo(invoice.id)}>
                      <Undo />
                    </IconButton>
                  ) : (
                    <IconButton color="error" onClick={() => handleDelete(invoice.id)}>
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelDisplayedRows={({ page }) =>
            `Page ${page + 1} of ${Math.ceil(total / rowsPerPage)}`
          }
        />
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
}
